import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchSfeData, SubmitLead, fetchLeadDataDetails } from '../../slices/eligibility/eligibilitySlice';
import ImageComponent from '../../components/common/Image';
import Loader from '../../components/common/Loader';
import { setApplicationDetail } from '../../slices/dashboard/dashboardSlice';

function Details() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userData } = useSelector((state) => state.auth);

    const userName = userData?.response_data?.data?.userName || '';
    const userID = userData?.response_data?.data?.userID || '';

    const urlParams = new URLSearchParams(window.location.search);
    const referenceId = urlParams.get('loanId');

    const [response, setResponse] = useState(null);

    // BUG FIX #4 & #5: Separate SFE data (dealers list) from remark/feedback options.
    // Previously both were written into `leadResponse` from the same fetch, causing
    // confusion and potential overwrite issues.
    const [remarksData, setRemarksData] = useState(null);   // holds remarks / feedback options
    const [sfeResponse, setSfeResponse] = useState([]);      // holds dealers list

    const [selectedReason, setSelectedReason] = useState('');
    const [tempSelectedDealerCode, setTempSelectedDealerCode] = useState('');
    const [tempSelectedDealerName, setTempSelectedDealerName] = useState('');
    const [customRemark, setCustomRemark] = useState('');

    // BUG FIX #6: was useState() → undefined; changed to '' to avoid
    // uncontrolled→controlled component warning and broken validation.
    const [reason, setReason] = useState('');

    const [loader, setLoader] = useState(false);
    const [feedbackType, setFeedbackType] = useState('');
    const [callbackDateTime, setCallbackDateTime] = useState('');
    const [remark, setRemark] = useState('');
    const [showNumber, setShowNumber] = useState(false);

    // ── Fetch lead details ─────────────────────────────────────────────────
    // BUG FIX #10: added `userID` to the dependency array — it is used inside
    // the callback but was missing, causing a stale-closure risk.
    const fetchStatusDetails = useCallback(async () => {
        if (!referenceId) return;
        setLoader(true);
        try {
            const res = await dispatch(fetchLeadDataDetails({
                tc_lead_id: referenceId,
                sfe_id: userID,
            })).unwrap();

            if (res?.response_code === 'S100') {
                if (!res?.response_data?.show_update_page) {
                    dispatch(setApplicationDetail(res?.response_data));
                    // BUG FIX #11: corrected typo 'eligiblity' → 'eligibility'
                    navigate('/eligiblity');
                } else {
                    setResponse(res.response_data);
                    setFeedbackType(res?.response_data?.disposition ?? '')
                    setCallbackDateTime(res?.response_data?.callback_time ?? '')
                    setRemark(res?.response_data?.customer_remarks ?? '')
                    setReason(res?.response_data?.reason ?? '')
                    setCustomRemark(res?.response_data?.remark ?? '')
                }
            } else {
                toast.error(res?.response_string || 'Failed to fetch lead details');
            }
        } catch {
            toast.error('An error occurred while fetching lead data');
        } finally {
            setLoader(false);
        }
    }, [referenceId, userID, dispatch, navigate]);

    // ── Fetch SFE / dealer data + remarks (depends on lead response) ────────
    // BUG FIX #3 & #4: remarks were previously either commented-out or mixed
    // into the same state as SFE data. Now we correctly split them:
    //   • sfeResponse  ← dealers list  (from res.response_data.dealers)
    //   • remarksData  ← feedback keys (from res.response_data.remarks)
    const fetchSfeDataDetails = useCallback(async () => {
        if (!response?.sfe_id || !response?.dealer_code) return;
        setLoader(true);
        try {
            const res = await dispatch(fetchSfeData({
                sfe_id: response.sfe_id,
                dealer_id: response.dealer_code,
            })).unwrap();

            if (res?.response_code === 'S100') {
                setSfeResponse(res.response_data);   // full payload kept for dealers
                setRemarksData(res.response_data.remarks ?? null); // BUG FIX #4: only remarks
            } else {
                toast.error(res?.response_string);
            }
        } catch (error) {
            console.error('SFE fetch error:', error);
        } finally {
            setLoader(false);
        }
    }, [dispatch, response?.sfe_id, response?.dealer_code]);

    // ── Effects ────────────────────────────────────────────────────────────
    useEffect(() => {
        fetchStatusDetails();
    }, [fetchStatusDetails]);

    useEffect(() => {
        if (response?.sfe_id && response?.dealer_code) {
            fetchSfeDataDetails();
        }
    }, [response?.sfe_id, response?.dealer_code, fetchSfeDataDetails]);

    // ── Submit ─────────────────────────────────────────────────────────────
    const submitDetails = async (action) => {
        setLoader(true);
        try {
            const finalRemarkValue = reason.toLowerCase() === 'others' ? customRemark : remark;

            const resp = await dispatch(SubmitLead({
                loan_id: response?.loan_id,
                tc_lead_id: response?.tc_lead_id,
                dealer_id: tempSelectedDealerCode,
                feedback: feedbackType,
                remarks: finalRemarkValue, // <--- Sent to API
                reason: reason,
                action,
                callback_time: callbackDateTime,
            })).unwrap();

            if (resp?.response_code === 'S100') {
                if (resp?.response_data?.redirect) {
                    navigate('/leads');
                } else {
                    dispatch(setApplicationDetail(resp?.response_data));
                    // BUG FIX #11: corrected typo 'eligiblity' → 'eligibility'
                    navigate('/eligiblity');
                }
            } else {
                toast.error(resp?.data?.response_string || 'Submission failed');
            }
        } catch {
            toast.error('An error occurred while submitting');
        } finally {
            setLoader(false);
        }
    };

    const handleDealerSelection = (dealerCode, dealerName) => {
        setTempSelectedDealerCode(dealerCode);
        setTempSelectedDealerName(dealerName);
    };

    // Mask a mobile number: show last 4 digits, mask the rest
    const maskMobileNo = (number) => {
        if (!number) return '';
        return number.slice(0, -4).replace(/\d/g, '•') + number.slice(-4);
    };

    // ── Derived flags (clean, single source of truth) ──────────────────────
    const remarkLower = remark?.toLowerCase();
    const typeUpper = feedbackType?.toUpperCase();

    // FIX: was incorrectly 'NOT_CONNECTED' — the actual API key is 'NOT_CONTACTED'
    const isNotContacted = typeUpper === 'NOT_CONTACTED';
    const isCallbackRequested = remarkLower === 'callback requested';
    const isNotInterested = remarkLower === 'not interested';
    const isInterested = remarkLower === 'interested';

    // Store should be hidden when:
    //   a) feedbackType is NOT_CONTACTED (caller was never reached)  OR
    //   b) remark is "Not Interested" or "Callback Requested"
    //      (those flows use different sub-fields, not a store)
    const showStoreSelect =
        feedbackType &&
        !isNotContacted &&
        !isNotInterested &&
        !isCallbackRequested && isInterested;

    // ── Validation ─────────────────────────────────────────────────────────
    const isSubmitDisabled = (() => {
        if (!feedbackType || !remark) return true;

        if (isCallbackRequested) return !callbackDateTime;

        // "Not Interested" flow logic
        if (isNotInterested) {
            if (!reason) return true;
            // If "Others" is selected, require the custom remark text field to be filled
            if (reason.toLowerCase() === 'others') return !customRemark.trim();
            return false;
        }

        if (!isNotContacted) return !tempSelectedDealerCode;

        return false;
    })();

    const handleTypeChange = (e) => {
        setFeedbackType(e.target.value);
        setRemark('');
        setReason('');
        setCustomRemark(''); // <--- Add this line
        setTempSelectedDealerCode('');
        setTempSelectedDealerName('');
        setCallbackDateTime('');
    };

    console.log(response, feedbackType, remark)

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <>
            {loader && <Loader />}

            <div className="position-relative">
                <header className="sticky-top rounded-0">
                    <div className="container-fluid topNav-dash">
                        <div className="web-header" />
                        <div className="mob-header">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="text-white fs-6 d-flex py-2 mt-2 align-items-center gap-1">
                                    <span
                                        className="icon-arrowleftl1 fs-2"
                                        onClick={() => navigate('/leads')}
                                        onKeyDown={(e) =>
                                            (e.key === 'Enter' || e.key === ' ') && navigate('/leads')
                                        }
                                        tabIndex={0}
                                        role="button"
                                    />
                                    {response?.status?.replace(/^./, (c) => c.toUpperCase())}
                                </div>
                            </div>
                        </div>
                    </div>
                </header>
            </div>

            <div className="container-fluid kyc-tab-dash-app height-scroll text-center">
                {/* BUG FIX #9: only show the in-progress image for relevant statuses */}
                {response?.status && response.status.toLowerCase() === 'inprogress' && (
                    <ImageComponent fileName="Inprogress.png" width="250px" height="250px" />
                )}

                <p className="fw-normal mt-3 fs-6 lh-base">
                    The Lead{' '}
                    <span className="dark-blue fw-semibold">{response?.reference_id}</span>{' '}
                    for the Customer{' '}
                    <span className="dark-blue fw-semibold">
                        {response?.first_name} {response?.last_name}
                    </span>{' '}
                    is {response?.status}
                </p>
                {(console.log(response?.e_mail, "eeee"))}

                <div className="exist-pop">
                    {response?.mobile_number && (
                        <div className="col-12 col-lg-12 col-md-12">
                            <p className="d-flex justify-content-between align-items-center">
                                <span>Mobile No.</span>
                                <span
                                    className="dark-blue fw-semibold d-flex align-items-center"
                                    onClick={() => setShowNumber((prev) => !prev)}
                                    onKeyDown={(e) =>
                                        (e.key === 'Enter' || e.key === ' ') &&
                                        setShowNumber((prev) => !prev)
                                    }
                                    tabIndex={0}
                                    role="button"
                                    style={{ cursor: 'pointer' }}
                                >
                                    {showNumber
                                        ? response.mobile_number
                                        : maskMobileNo(response.mobile_number)}
                                    <i
                                        className={`ms-1 ${showNumber ? 'icon-securityeye-slash' : 'icon-securityeye'}`}
                                    />
                                </span>
                            </p>
                        </div>
                    )}

                    {response?.e_mail && (
                        // BUG FIX #7: was 'd-none col-12' run together as 'd-nonecol-12'
                        <div className="col-12 col-lg-12 col-md-12">
                            <p className="d-flex justify-content-between">
                                E Mail{' '}
                                <span className="dark-blue fw-semibold">{response.e_mail}</span>
                            </p>
                        </div>
                    )}

                    {response?.product_type && (
                        // BUG FIX #7: same className typo fixed
                        <div className="col-12 col-lg-12 col-md-12">
                            <p className="d-flex justify-content-between">
                                Existing Product{' '}
                                <span className="dark-blue fw-semibold">
                                    {response.product_type}
                                </span>
                            </p>
                        </div>
                    )}

                    {response?.interest && (
                        <div className="col-12 col-lg-12 col-md-12">
                            <p className="d-flex justify-content-between">
                                Interested Product{' '}
                                <span className="dark-blue fw-semibold">{response.interest}</span>
                            </p>
                        </div>
                    )}

                    {response?.brand && (
                        <div className="col-12 col-lg-12 col-md-12">
                            <p className="d-flex justify-content-between">
                                Brand{' '}
                                <span className="dark-blue fw-semibold">{response.brand}</span>
                            </p>
                        </div>
                    )}

                    {response?.pincode && (
                        <div className="col-12 col-lg-12 col-md-12">
                            <p className="d-flex justify-content-between">
                                Customer Pincode{' '}
                                <span className="dark-blue fw-semibold">{response.pincode}</span>
                            </p>
                        </div>
                    )}

                    {response?.loan_id && (
                        <div className="col-12 col-lg-12 col-md-12">
                            <p className="d-flex justify-content-between">
                                Existing Loan ID{' '}
                                <span className="dark-blue fw-semibold">{response.loan_id}</span>
                            </p>
                        </div>
                    )}

                    {response?.pre_approved_amt && (
                        <div className="col-12 col-lg-12 col-md-12">
                            <p className="d-flex justify-content-between">
                                Pre Approved Amount{' '}
                                <span className="dark-blue fw-semibold">
                                    {response.pre_approved_amt}
                                </span>
                            </p>
                        </div>
                    )}
                </div>

                {/* ── Contactability + Customer Feedback dropdowns ───────── */}
                {/* BUG FIX #4: now reads from `remarksData` (not `leadResponse`) */}
                {remarksData && (
                    <>
                        <div>
                            <label className="form-label fw-semibold">Contactability</label>
                            <select
                                value={feedbackType}
                                onChange={handleTypeChange}
                                className="border p-2 rounded w-64"
                            >
                                <option value="">Select Type</option>
                                {Object.keys(remarksData).map((type) => (
                                    <option key={type} value={type}>
                                        {type
                                            .split('_')
                                            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                            .join(' ')
                                        }
                                    </option>
                                ))}
                            </select>
                        </div>

                        {feedbackType && (
                            <div>
                                <label className="form-label fw-semibold">Customer Feedback</label>
                                <select
                                    value={remark}
                                    onChange={(e) => setRemark(e.target.value)}
                                    disabled={!feedbackType}
                                    className="border p-2 rounded w-64"
                                >
                                    <option value="">Select Feedback</option>
                                    {Object.keys(remarksData?.[feedbackType] ?? {}).map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </>
                )}

                {/* ── Store select ───────────────────────────────────────── */}
                {/* BUG FIX #1: condition now correctly hides store for NOT_CONNECTED,
                    "Not Interested", and "Callback Requested" flows */}
                {showStoreSelect && (
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Select Store</label>
                        <select
                            className="form-select"
                            value={tempSelectedDealerCode}
                            onChange={(e) => {
                                const selectedDealer = sfeResponse?.dealers?.find(
                                    (d) => d.dealer_code === e.target.value
                                );
                                if (selectedDealer) {
                                    handleDealerSelection(
                                        selectedDealer.dealer_code,
                                        selectedDealer.dealer_name
                                    );
                                }
                            }}
                        >
                            <option value="">Select Store</option>
                            {sfeResponse?.dealers?.length > 0 ? (
                                sfeResponse.dealers.map((dealer, index) => (
                                    <option key={index} value={dealer.dealer_code}>
                                        {dealer.dealer_code} - {dealer.dealer_name}
                                    </option>
                                ))
                            ) : (
                                <option disabled>No stores available</option>
                            )}
                        </select>
                    </div>
                )}

                {/* ── "Not Interested" reason sub-select ────────────────── */}
                {isNotInterested && (
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Reason</label>
                        <select
                            value={reason}
                            onChange={(e) => {
                                setReason(e.target.value);
                                setCustomRemark(''); // Clear text if they toggle away from Others
                            }}
                            disabled={!feedbackType}
                            className="border p-2 rounded w-64 form-select"
                        >
                            <option value="">Select Reason</option>
                            {remarksData?.[feedbackType]?.[remark]?.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* ── Custom Remarks text box when "Others" is chosen ───── */}
                {isNotInterested && reason.toLowerCase() === 'others' && (
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Please specify your remark</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            placeholder="Enter reason details here..."
                            value={customRemark}
                            onChange={(e) => setCustomRemark(e.target.value)}
                        />
                    </div>
                )}

                {/* ── Callback date/time picker ──────────────────────────── */}
                {isCallbackRequested && (
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Callback Date &amp; Time</label>
                        <input
                            type="datetime-local"
                            className="form-control"
                            value={callbackDateTime}
                            min={new Date().toISOString().slice(0, 16)}
                            onChange={(e) => setCallbackDateTime(e.target.value)}
                        />
                        <small className="text-muted">
                            Please schedule the preferred time for the callback.
                        </small>
                    </div>
                )}

                {/* ── Re-Assign button ───────────────────────────────────── */}
                {/* BUG FIX #8: removed conflicting `d-none` that permanently hid button */}
                <div
                    className="d-none d-flex justify-content-center align-items-stretch gap-3 mt-3"
                    style={{ width: '100%' }}
                >
                    <button
                        className="btn btn-outline-primary w-100 text-truncate Details-page-btns"
                        disabled={isSubmitDisabled}
                        onClick={() => submitDetails('reject')}
                    >
                        Re-Assign
                    </button>
                </div>

                {/* ── Proceed button ─────────────────────────────────────── */}
                <div
                    className="d-flex justify-content-center align-items-stretch gap-3 mt-3"
                    style={{ width: '100%' }}
                >
                    <button
                        className="btn btn-primary w-100 text-truncate Details-page-btns"
                        disabled={isSubmitDisabled}
                        onClick={() => submitDetails('approve')}
                    >
                        Proceed
                    </button>
                </div>
            </div>
        </>
    );
}

export default Details;
